// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useListAdvisors } from '@shared/advisory/aggregation/components/advisors/use-advisors';

export const useShareAccount = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { advisors, isLoading: isLoadingAdvisors } = useListAdvisors();
  const [shareAccounts, { isLoading: isSharing }] =
    advisoryApi.useShareAdvisoryUsersAccountsMutation();

  const handleShareClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setIsShareOpen(true);
    },
    [],
  );

  const handleShareClose = useCallback(() => {
    setIsShareOpen(false);
    setEmail('');
    setAnchorEl(null);
  }, []);

  const handleShare = useCallback(
    async (
      ibans: string | string[],
      productName?: string,
      targetEmail?: string,
    ): Promise<boolean> => {
      const finalEmail = targetEmail || email;
      const ibanArray = Array.isArray(ibans) ? ibans : [ibans];

      if (!finalEmail || ibanArray.length === 0) return false;

      try {
        await shareAccounts({
          user_email: finalEmail,
          ibans: ibanArray,
        }).unwrap();

        const isSingleWithTitle = ibanArray.length === 1 && !!productName;
        const translationKey = isSingleWithTitle
          ? 'aggregator.share-institutions-success-details-one'
          : 'aggregator.share-institutions-success-details';

        enqueueSnackbar(
          t(translationKey, {
            name: productName,
            email: finalEmail,
            count: ibanArray.length,
            ns: 'aggregator',
          }),
          { variant: 'success' },
        );

        handleShareClose();
        return true;
      } catch {
        enqueueSnackbar(
          t('aggregator.share-institutions-error', { ns: 'aggregator' }),
          { variant: 'error' },
        );
        return false;
      }
    },
    [email, shareAccounts, t, enqueueSnackbar, handleShareClose],
  );

  return {
    isShareOpen,
    email,
    setEmail,
    anchorEl,
    isSharing,
    advisors,
    isLoadingAdvisors,
    handleShareClick,
    handleShareClose,
    handleShare,
  };
};
